import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, LogOut, Download, Loader2, ShieldCheck } from "lucide-react";
import Seo from "../components/Seo";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "mb_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [subs, setSubs] = useState([]);
  const [count, setCount] = useState(0);
  const [loadErr, setLoadErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setSubs([]);
    setCount(0);
  }, []);

  const loadSubs = useCallback(async (t) => {
    setLoading(true);
    setLoadErr("");
    try {
      const res = await axios.get(`${API}/api/admin/subscribers`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setSubs(res.data.subscribers || []);
      setCount(res.data.count || 0);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        setLoadErr("Sessie verlopen. Log opnieuw in.");
      } else {
        setLoadErr("Kon inschrijvingen niet laden.");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) loadSubs(token);
  }, [token, loadSubs]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const res = await axios.post(`${API}/api/admin/login`, { username, password });
      const t = res.data.token;
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setUsername("");
      setPassword("");
    } catch (err) {
      setLoginError(err?.response?.data?.detail || "Login mislukt");
    } finally {
      setLoggingIn(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/api/admin/subscribers/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "mechelen-beach-nieuwsbrief-inschrijvingen.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    } finally {
      setExporting(false);
    }
  };

  // ---------- LOGIN VIEW ----------
  if (!token) {
    return (
      <>
        <Seo title="Admin | Mechelen Beach" description="Beveiligde adminpagina" noindex />
        <section className="min-h-screen bg-warm-gradient flex items-center justify-center px-4 py-16">
          <form
            onSubmit={onLogin}
            data-testid="admin-login-form"
            className="w-full max-w-md rounded-2xl border border-[#ebd9b5] bg-white p-6 sm:p-8 shadow-[0_18px_44px_-18px_rgba(43,28,16,0.35)]"
          >
            <div className="text-center">
              <div className="mx-auto h-12 w-12 rounded-full bg-orange-gradient text-white flex items-center justify-center">
                <Lock className="h-6 w-6" />
              </div>
              <h1 className="mt-4 font-display text-2xl sm:text-3xl font-semibold text-[#2b1c10]">
                Admin login
              </h1>
              <p className="mt-2 text-sm text-[#6b5945]">
                Log in om de nieuwsbriefinschrijvingen te bekijken.
              </p>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#2b1c10] mb-1.5">
                  Gebruikersnaam
                </label>
                <input
                  type="text"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  data-testid="admin-username"
                  className="w-full px-3 py-3 rounded-xl border border-[#ebd9b5] bg-[#fdf8ec] text-[#2b1c10] focus:outline-none focus:border-[#ed6a1a] focus:ring-2 focus:ring-[#ed6a1a]/20 transition"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#2b1c10] mb-1.5">
                  Wachtwoord
                </label>
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password"
                  className="w-full px-3 py-3 rounded-xl border border-[#ebd9b5] bg-[#fdf8ec] text-[#2b1c10] focus:outline-none focus:border-[#ed6a1a] focus:ring-2 focus:ring-[#ed6a1a]/20 transition"
                />
              </div>
              {loginError && (
                <p
                  data-testid="admin-login-error"
                  className="text-sm text-[#c54508] bg-[#fde3d5] border border-[#f5b494] rounded-lg px-3 py-2"
                >
                  {loginError}
                </p>
              )}
              <button
                type="submit"
                disabled={loggingIn || !username || !password}
                data-testid="admin-login-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-orange-gradient text-white px-7 py-3.5 text-sm sm:text-base font-semibold shadow-[0_10px_28px_-8px_rgba(237,106,26,0.55)] hover:brightness-110 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : "Inloggen"}
              </button>
            </div>
          </form>
        </section>
      </>
    );
  }

  // ---------- DASHBOARD VIEW ----------
  return (
    <>
      <Seo title="Admin | Mechelen Beach" description="Nieuwsbriefinschrijvingen" noindex />
      <section className="bg-warm-gradient min-h-screen py-10 sm:py-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#ed6a1a]">
                <ShieldCheck className="h-3.5 w-3.5" />
                Admin
              </span>
              <h1
                data-testid="admin-title"
                className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-[#2b1c10]"
              >
                Nieuwsbriefinschrijvingen
              </h1>
              <p
                data-testid="admin-count"
                className="mt-2 text-sm text-[#6b5945]"
              >
                Totaal aantal inschrijvingen: <strong>{count}</strong>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={exportCsv}
                disabled={exporting || count === 0}
                data-testid="admin-export-csv"
                className="inline-flex items-center gap-2 rounded-full bg-orange-gradient text-white px-5 py-2.5 text-sm font-semibold shadow-[0_10px_28px_-8px_rgba(237,106,26,0.5)] hover:brightness-110 transition disabled:opacity-50"
              >
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Export CSV
              </button>
              <button
                type="button"
                onClick={logout}
                data-testid="admin-logout"
                className="inline-flex items-center gap-2 rounded-full border border-[#ebd9b5] bg-white text-[#2b1c10] px-5 py-2.5 text-sm font-semibold hover:border-[#ed6a1a] transition"
              >
                <LogOut className="h-4 w-4" />
                Uitloggen
              </button>
            </div>
          </div>

          {loadErr && (
            <p className="mt-6 text-sm text-[#c54508] bg-[#fde3d5] border border-[#f5b494] rounded-lg px-3 py-2">
              {loadErr}
            </p>
          )}

          <div className="mt-8 rounded-2xl border border-[#ebd9b5] bg-white overflow-hidden shadow-[0_14px_36px_-18px_rgba(43,28,16,0.25)]">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-subscribers-table">
                <thead className="bg-[#fdf8ec] text-[#2b1c10]">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">E-mailadres</th>
                    <th className="text-left px-4 py-3 font-semibold">Interessegebieden</th>
                    <th className="text-left px-4 py-3 font-semibold">Toestemming</th>
                    <th className="text-left px-4 py-3 font-semibold">Datum/tijd</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f0e4cf]">
                  {loading && (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-[#6b5945]">
                        <Loader2 className="h-5 w-5 animate-spin inline-block" />
                      </td>
                    </tr>
                  )}
                  {!loading && subs.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-[#6b5945]">
                        Nog geen inschrijvingen.
                      </td>
                    </tr>
                  )}
                  {!loading && subs.map((s) => (
                    <tr key={s.id || s.email} className="hover:bg-[#fdf8ec]/60">
                      <td className="px-4 py-3 text-[#2b1c10] break-all">{s.email}</td>
                      <td className="px-4 py-3 text-[#4a3b2c]">
                        {(s.interests || []).join(", ") || "—"}
                      </td>
                      <td className="px-4 py-3 text-[#4a3b2c]">
                        {s.consent ? "Ja" : "Nee"}
                      </td>
                      <td className="px-4 py-3 text-[#6b5945] whitespace-nowrap">
                        {s.created_at ? new Date(s.created_at).toLocaleString("nl-BE", { timeZone: "Europe/Brussels" }) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
